import { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../Firebase";
import {
  doc,
  collection,
  setDoc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const CartWishlistContext = createContext();

export const CartWishlistProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  const [cartCourses, setCartCourses] = useState([]);
  const [wishlistCourses, setWishlistCourses] = useState([]);

  /* ================= AUTH ================= */
  useEffect(() => {
    return onAuthStateChanged(auth, setUser);
  }, []);

  /* ================= FETCH COURSE DETAILS ================= */
  const fetchCourses = async (ids, setter) => {
    const docs = await Promise.all(
      ids.map(id => getDoc(doc(db, "courses", id)))
    );

    setter(
      docs
        .filter(d => d.exists())
        .map(d => ({ id: d.id, ...d.data() }))
    );
  };

  /* ================= CART REALTIME ================= */
  useEffect(() => {
    if (!user) return;

    const cartRef = doc(db, "cart", user.uid);

    return onSnapshot(cartRef, async snap => {
      if (!snap.exists()) return;

      setCartCount(snap.data().count || 0);

      const itemsSnap = await getDocs(
        collection(db, "cart", user.uid, "items")
      );

      const ids = itemsSnap.docs.map(d => d.id);
      fetchCourses(ids, setCartCourses);
    });
  }, [user]);

  /* ================= WISHLIST REALTIME ================= */
  useEffect(() => {
    if (!user) return;

    const wishRef = doc(db, "wishlist", user.uid);

    return onSnapshot(wishRef, async snap => {
      if (!snap.exists()) return;

      setWishlistCount(snap.data().count || 0);

      const itemsSnap = await getDocs(
        collection(db, "wishlist", user.uid, "items")
      );

      const ids = itemsSnap.docs.map(d => d.id);
      fetchCourses(ids, setWishlistCourses);
    });
  }, [user]);

  /* ================= ADD TO CART ================= */
  const addToCart = async course => {
    if (!user) return alert("Login first");

    const itemRef = doc(
      db,
      "cart",
      user.uid,
      "items",
      course.id
    );

    const exists = await getDoc(itemRef);
    if (exists.exists()) return alert("Already in cart");

    await setDoc(itemRef, { courseId: course.id });

    await setDoc(
      doc(db, "cart", user.uid),
      { count: cartCount + 1 },
      { merge: true }
    );
  };

  /* ================= REMOVE CART ================= */
  const removeFromCart = async id => {
    await deleteDoc(
      doc(db, "cart", user.uid, "items", id)
    );

    await setDoc(
      doc(db, "cart", user.uid),
      { count: Math.max(cartCount - 1, 0) },
      { merge: true }
    );
  };

  /* ================= ADD WISHLIST ================= */
  const addToWishlist = async course => {
    if (!user) return alert("Login first");

    const itemRef = doc(
      db,
      "wishlist",
      user.uid,
      "items",
      course.id
    );

    const exists = await getDoc(itemRef);
    if (exists.exists()) return alert("Already wishlisted");

    await setDoc(itemRef, { courseId: course.id });

    await setDoc(
      doc(db, "wishlist", user.uid),
      { count: wishlistCount + 1 },
      { merge: true }
    );
  };

  /* ================= REMOVE WISHLIST ================= */
  const removeFromWishlist = async id => {
    await deleteDoc(
      doc(db, "wishlist", user.uid, "items", id)
    );

    await setDoc(
      doc(db, "wishlist", user.uid),
      { count: Math.max(wishlistCount - 1, 0) },
      { merge: true }
    );
  };

  return (
    <CartWishlistContext.Provider
      value={{
        cartCourses,
        wishlistCourses,
        cartCount,
        wishlistCount,
        addToCart,
        addToWishlist,
        removeFromCart,
        removeFromWishlist
      }}
    >
      {children}
    </CartWishlistContext.Provider>
  );
};

export const useCartWishlist = () =>
  useContext(CartWishlistContext);
